// src/features/profile/services/hexagramService.js
// Hexagram calculation and astrology service

import {
  calculateHexagramProfile,
  getHexagramDetails,
  getDailyHexagram,
  getJournalPrompt,
  getAIGuidanceContext,
  calculateAstrologicalProfile
} from '../utils';
import { calculateZodiacSign } from '@/shared/hooks/useAIFeedback';
import logger from '@/shared/utils/core/logger';

/**
 * Calculate complete hexagram profile with astronomical data
 * @param {Object} profileData - Profile data (birthDate, birthTime, birthPlace)
 * @returns {Promise<Object|null>} - Complete hexagram profile or null
 */
export async function calculateCompleteHexagramProfile(profileData) {
  if (!profileData?.birthDate) {
    logger.warn('[HexagramService] No birth date provided');
    return null;
  }

  try {
    logger.info('🔮 [HexagramService] Calculating hexagrams...');

    let hexProfile = null;

    // Try astronomical calculation ONLY if we have time, place, AND coordinates.
    // Use != null so lon=0 (Prime Meridian) is treated as valid, not falsy.
    if (profileData.birthTime && profileData.birthPlace &&
        profileData.birthPlaceLat != null && profileData.birthPlaceLon != null) {
      try {
        const astroProfile = await calculateAstrologicalProfile(
          profileData.birthDate,
          profileData.birthTime,
          profileData.birthPlace,
          profileData.birthPlaceLat,
          profileData.birthPlaceLon,
          profileData.birthTimezone
        );

        if (astroProfile) {
          hexProfile = buildAstronomicalHexagramProfile(astroProfile);
        }
      } catch (astroError) {
        logger.warn('⚠️ [HexagramService] Astronomical calculation failed:', astroError);
      }
    } else if (profileData.birthTime && profileData.birthPlace) {
      // Has text location but missing coordinates - skip astro calculation
      logger.warn('⚠️ [HexagramService] Birth location text exists but coordinates missing. Please update birth location in settings for astrological features.');
    }

    // Fallback to simple calculation if enhanced fails or data is incomplete
    if (!hexProfile) {
      hexProfile = calculateHexagramProfile(
        profileData.birthDate,
        profileData.birthTime,
        profileData.birthPlace
      );

      if (hexProfile) {
        hexProfile = addHexagramDetails(hexProfile);
      } else {
        logger.error('❌ [HexagramService] Simple calculation returned null');
      }
    }

    if (hexProfile) {
      // Add zodiac sign
      hexProfile = addZodiacSign(hexProfile, profileData.birthDate);
    }

    return hexProfile;
  } catch (error) {
    logger.error('❌ [HexagramService] Error calculating hexagram profile:', error);
    throw error;
  }
}

/**
 * Build hexagram profile from astronomical data
 * @param {Object} astroProfile - Astronomical profile data
 * @returns {Object} - Hexagram profile
 */
function buildAstronomicalHexagramProfile(astroProfile) {
  const { humanDesignGates, humanDesign, westernAstrology, planets, fourPillars } = astroProfile;

  // Build westernAstrology object only with defined values.
  // calculateWesternAstrology() returns { sun: {sign,degree,...}, moon: {...}, rising: {...}|null }
  const westAstro = {};
  if (westernAstrology?.sun)    westAstro.sun    = westernAstrology.sun;
  if (westernAstrology?.moon)   westAstro.moon   = westernAstrology.moon;
  if (westernAstrology?.rising) westAstro.rising = westernAstrology.rising;

  // Build natalChart object only with planets that were successfully calculated
  const natalChartData = {};
  if (planets?.sun) natalChartData.sun = planets.sun;
  if (planets?.moon) natalChartData.moon = planets.moon;
  if (planets?.mercury) natalChartData.mercury = planets.mercury;
  if (planets?.venus) natalChartData.venus = planets.venus;
  if (planets?.mars) natalChartData.mars = planets.mars;
  if (planets?.jupiter) natalChartData.jupiter = planets.jupiter;
  if (planets?.saturn) natalChartData.saturn = planets.saturn;
  if (planets?.uranus) natalChartData.uranus = planets.uranus;
  if (planets?.neptune) natalChartData.neptune = planets.neptune;
  if (planets?.pluto) natalChartData.pluto = planets.pluto;
  if (planets?.northNode) natalChartData.northNode = planets.northNode;
  if (planets?.southNode) natalChartData.southNode = planets.southNode;

  const hexProfile = {
    personality: humanDesignGates.sun?.gate || 1,
    design: humanDesignGates.moon?.gate || 1,
    life_purpose: humanDesignGates.northNode?.gate || 1,
    relationships: humanDesignGates.moon?.gate || 1,

    // Full Western Astrology (Co-Star style) - only defined values
    ...(Object.keys(westAstro).length > 0 && { westernAstrology: westAstro }),

    // Full Human Design (MyBodyGraph style)
    humanDesign: {
      type: humanDesign?.type || 'Unknown',
      profile: humanDesign?.profile || 'Unknown',
      profileMeaning: humanDesign?.profileMeaning || 'Unknown',
      strategy: humanDesign?.strategy || 'Unknown',
      authority: humanDesign?.authority || 'Unknown',
      definition: humanDesign?.definition || 'Unknown',
      incarnationCross: humanDesign?.incarnationCross || 'Unknown',
      gates: humanDesignGates || {} // All planetary gates
    },

    // Complete natal chart (all 10 planets) - only successfully calculated
    ...(Object.keys(natalChartData).length > 0 && { natalChart: natalChartData }),

    // Chinese Four Pillars (BaZi) - only if present
    ...(fourPillars && { fourPillars }),

    // Birth data for reference - only if present
    ...(astroProfile.birthData && { birthData: astroProfile.birthData }),

    calculatedAt: new Date().toISOString(),
    calculationMethod: 'astronomical'
  };

  // Add hexagram details
  return addHexagramDetails(hexProfile);
}

/**
 * Add hexagram details to profile
 * @param {Object} hexProfile - Hexagram profile
 * @returns {Object} - Profile with details added
 */
function addHexagramDetails(hexProfile) {
  return {
    ...hexProfile,
    personalityDetails: getHexagramDetails(hexProfile.personality),
    designDetails: getHexagramDetails(hexProfile.design),
    lifePurposeDetails: getHexagramDetails(hexProfile.life_purpose),
    relationshipsDetails: getHexagramDetails(hexProfile.relationships)
  };
}

/**
 * Add zodiac sign to hexagram profile
 * @param {Object} hexProfile - Hexagram profile
 * @param {string} birthDate - Birth date
 * @returns {Object} - Profile with zodiac sign added
 */
function addZodiacSign(hexProfile, birthDate) {
  try {
    const birthDateObj = new Date(birthDate);
    const zodiacSign = calculateZodiacSign(birthDateObj);
    hexProfile.zodiacSign = zodiacSign;
  } catch (zodiacError) {
    logger.warn('⚠️ [HexagramService] Failed to calculate zodiac sign:', zodiacError);
  }

  return hexProfile;
}

/**
 * Calculate daily hexagram
 * @param {string} birthDate - Birth date
 * @param {string} birthTime - Birth time (optional)
 * @returns {Object|null} - Daily hexagram data or null
 */
export function calculateDailyHexagram(birthDate, birthTime) {
  if (!birthDate) {
    logger.warn('[HexagramService] No birth date for daily hexagram');
    return null;
  }

  try {
    const daily = getDailyHexagram(birthDate, birthTime);
    return daily;
  } catch (error) {
    logger.error('❌ [HexagramService] Error calculating daily hexagram:', error);
    return null;
  }
}

/**
 * Get personalized journal prompt
 * @param {Object|null} dailyHexagram - Daily hexagram data
 * @param {string} context - Context for prompt (default: 'general')
 * @returns {string} - Personalized prompt
 */
export function getPersonalizedJournalPrompt(dailyHexagram, context = 'general') {
  if (!dailyHexagram || !dailyHexagram.number) {
    return "What's on your mind today? Let your thoughts flow freely...";
  }

  const prompt = getJournalPrompt(dailyHexagram.number, context);
  return prompt;
}

/**
 * Get AI guidance context
 * @param {Object|null} hexagramProfile - Hexagram profile
 * @param {string} journalContent - Journal content
 * @returns {Object|null} - AI context or null
 */
export function getAIContext(hexagramProfile, journalContent = '') {
  if (!hexagramProfile) {
    return null;
  }

  return getAIGuidanceContext(hexagramProfile, journalContent);
}
